import { useMemo, useState } from "react";
import { CheckSquare, Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "~/atoms/button";
import { Checkbox } from "~/atoms/checkbox";
import { ScrollArea } from "~/atoms/scroll-area";

import { type WorkflowTemplateListItem } from "~/api/types";
import { cn } from "~/lib/utils";
import { toggleSetItem } from "~/helpers/set";
import { Skeleton } from "~/atoms/skeleton";
import { Toggle } from "~/atoms/toggle";

interface Props {
  templates: WorkflowTemplateListItem[];
  selectedTemplateId: number | null;
  onSelect: (id: number) => void;
  onCreate: () => void;
  onDelete: (id: number) => void;
  onBulkDelete: (ids: number[]) => void;
}

export function WorkflowTemplateSidebar({
  templates,
  selectedTemplateId,
  onSelect,
  onCreate,
  onDelete,
  onBulkDelete,
}: Props) {
  const { t } = useTranslation();
  const [selectMode, setSelectMode] = useState(false);
  const [checkedIds, setCheckedIds] = useState<Set<number>>(new Set());

  const sortedTemplates = useMemo(() => {
    const defaultTemplate = templates.find((t) => t.isDefault);
    const rest = templates.filter((t) => !t.isDefault);
    return defaultTemplate ? [defaultTemplate, ...rest] : rest;
  }, [templates]);

  const toggleSelectMode = () => {
    if (selectMode) {
      setCheckedIds(new Set());
    }
    setSelectMode(!selectMode);
  };

  const toggleChecked = (id: number) => {
    setCheckedIds((prev) => toggleSetItem(prev, id));
  };

  const handleBulkDeleteClick = () => {
    onBulkDelete([...checkedIds]);
    setCheckedIds(new Set());
    setSelectMode(false);
  };

  return (
    <aside className="flex flex-col">
      <div className="flex items-center justify-between px-4 py-1">
        <h2 className="text-sm font-semibold">
          {t(($) => $.workflowTemplates.navLabel)}
        </h2>
        <div className="flex items-center gap-1">
          {templates.some((t) => !t.isDefault) && (
            <Toggle onClick={toggleSelectMode}>
              <CheckSquare />
            </Toggle>
          )}
          <Button variant="ghost" size="icon" onClick={onCreate}>
            <Plus />
          </Button>
        </div>
      </div>

      <ScrollArea className="h-110">
        {sortedTemplates.length === 0 ? (
          <p className="text-muted-foreground px-4 py-1 text-center text-sm">
            {t(($) => $.workflowTemplates.noTemplates)}
          </p>
        ) : (
          <div className="p-2">
            {sortedTemplates.map((template) => {
              const isDefault = template.isDefault;

              return (
                <div
                  key={template.id}
                  onClick={() =>
                    selectMode
                      ? !isDefault && toggleChecked(template.id)
                      : onSelect(template.id)
                  }
                  className={cn(
                    "group flex w-full cursor-pointer items-center justify-between rounded-md border-l-2 px-3 py-2 text-left text-sm transition-all duration-200",
                    selectMode && checkedIds.has(template.id)
                      ? "border-destructive bg-destructive/10"
                      : selectedTemplateId === template.id && !selectMode
                        ? "border-primary bg-primary text-primary-foreground"
                        : "hover:bg-muted border-transparent",
                  )}
                >
                  <span className="flex items-center gap-2 truncate">
                    {!isDefault && (
                      <span
                        className={cn(
                          "shrink-0 overflow-hidden transition-all duration-200",
                          selectMode ? "w-4 opacity-100" : "w-0 opacity-0",
                        )}
                      >
                        <Checkbox
                          checked={checkedIds.has(template.id)}
                          onCheckedChange={() => toggleChecked(template.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="shrink-0"
                        />
                      </span>
                    )}
                    <span className="truncate font-medium">
                      {template.name ??
                        t(($) => $.workflowTemplates.defaultBadge)}
                    </span>
                    {isDefault && (
                      <span
                        className={cn(
                          "shrink-0 rounded-sm px-1.5 py-0.5 text-xs font-medium transition-colors duration-200",
                          selectedTemplateId === template.id && !selectMode
                            ? "bg-primary-foreground/20 text-primary-foreground"
                            : "bg-muted text-muted-foreground",
                        )}
                      >
                        {t(($) => $.workflowTemplates.defaultBadge)}
                      </span>
                    )}
                  </span>
                  {!isDefault && (
                    <span
                      className={cn(
                        "shrink-0 transition-all duration-200",
                        selectMode
                          ? "pointer-events-none w-0 overflow-hidden opacity-0"
                          : "opacity-0 group-hover:opacity-100",
                        selectedTemplateId === template.id
                          ? "text-primary-foreground/70 hover:text-primary-foreground"
                          : "text-muted-foreground hover:text-destructive",
                      )}
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(template.id);
                      }}
                    >
                      <Trash2 className="size-4" />
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </ScrollArea>

      <div
        className={cn(
          "overflow-hidden transition-all duration-200",
          selectMode && checkedIds.size > 0
            ? "max-h-14 opacity-100"
            : "max-h-0 opacity-0",
        )}
      >
        <div className="border-t px-4 py-2">
          <Button
            variant="destructive"
            size="sm"
            className="w-full"
            onClick={handleBulkDeleteClick}
          >
            {t(($) => $.workflowTemplates.deleteSelected, {
              count: checkedIds.size,
            })}
          </Button>
        </div>
      </div>
    </aside>
  );
}

export function WorkflowTemplateSidebarSkeleton() {
  return (
    <aside className="flex flex-col">
      <div className="flex items-center justify-between px-4 py-1">
        <Skeleton className="h-5 w-24" />
        <Skeleton className="size-7 rounded-md" />
      </div>

      <div className="p-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-center gap-2 rounded-md px-3 py-2">
            <Skeleton className="h-4 w-full" />
          </div>
        ))}
      </div>
    </aside>
  );
}
